import React, { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, UserPlus, ExternalLink, AlertCircle, History } from "lucide-react";
import { toast } from "sonner";

const SECTION_LABEL = { status: "Where it stands", advance: "Move it forward", route: "Who owns it", resolve: "Resolve" };
const SECTION_ORDER = ["status", "advance", "route", "resolve"];
const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `ev_${Date.now()}_${Math.random().toString(16).slice(2)}`);
const inputTestId = (name) => (name === "date" ? "triage-date" : (name === "reason" || name === "note") ? "triage-reason" : name === "owner" ? "triage-reassign-select" : `triage-input-${name}`);

// ONE reusable, adapter-driven Today triage control. It renders the adapter's menu and
// performs each mutation through the adapter's declared request builder — NO per-type
// mutation code lives here. History is recorded by the action itself on the backend;
// the control only sends an idempotency event_id so a retry can't double-write.
export function TodayTriage({ item, adapter, api, users = [], onDone, onOpenClient, onOpenBoard, onOpen, idx }) {
  const [open, setOpen] = useState(null); // menu item object, or {key:"__history"}
  const [vals, setVals] = useState({});
  const [eventId, setEventId] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState(null);

  const id = adapter.getId(item);
  const title = adapter.getTitle(item);
  const owner = adapter.getOwner ? adapter.getOwner(item) : "";
  const client = adapter.getClient ? adapter.getClient(item) : { id: "", name: "" };
  const gate = adapter.canTriage(item);
  const menu = adapter.menu || [];
  const reassign = menu.find((m) => m.key === "reassign");

  const close = () => { setOpen(null); setVals({}); setErr(""); setQ(""); setResults([]); setHistory(null); };

  const req = useCallback(async (path, method, body) => {
    const r = await fetch(`${api}${path}`, { method, credentials: "include", headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
    const b = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, body: b };
  }, [api]);

  const startAction = (m) => {
    setErr(""); setVals({}); setQ(""); setResults([]);
    setEventId(uuid()); // one id per user action; reused on retry within this dialog
    if (!m.inputs || m.inputs.length === 0) { runAction(m, {}, uuid()); return; }
    setOpen(m);
  };

  const runAction = async (m, values, eid) => {
    // validate required
    for (const inp of (m.inputs || [])) {
      if (inp.required && (values[inp.name] === undefined || values[inp.name] === "" || values[inp.name] === null)) {
        setErr(`${inp.label} is required.`); return;
      }
    }
    setBusy(true);
    const { path, method, body } = m.build(values, item, { eventId: eid || eventId, users });
    const { ok, status, body: res } = await req(path, method, body);
    setBusy(false);
    if (res && res.applied === false && res.reason === "no change") { toast.info("Nothing changed"); close(); onDone && onDone(); return; }
    if (res && res.applied === false && res.duplicate) { toast.success("Recorded"); close(); onDone && onDone(); return; }
    if (ok && res.success !== false) { toast.success("Done"); close(); onDone && onDone(); return; }
    if (status === 409) { toast.info("This one needs an outcome — open it"); return; }
    setErr(res?.detail?.message || res?.detail?.error || res?.error || (typeof res?.detail === "string" ? res.detail : "") || "Could not save");
  };

  const doSearch = async (term) => {
    setQ(term);
    if (!term || term.length < 2 || !adapter.relateSearch) { setResults([]); return; }
    const { ok, body } = await req(adapter.relateSearch(term), "GET");
    if (ok && Array.isArray(body)) setResults(body);
  };

  const openHistory = async () => {
    setOpen({ key: "__history" }); setHistory({ loading: true, rows: [] });
    const { ok, body } = await req(adapter.historyRead(id), "GET");
    setHistory({ loading: false, rows: ok ? (body.history || []) : [] });
  };

  // Fail closed: say why, keep the specialist doorway.
  if (!gate.ok) {
    return (
      <div className="flex items-center gap-1.5 shrink-0" data-testid={`triage-${idx}`}>
        <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 max-w-[20rem]" data-testid={`triage-blocked-${idx}`} title={gate.missing}>
          <AlertCircle className="h-3 w-3 shrink-0" /> <span className="truncate">{gate.missing}</span>
        </span>
        {adapter.specialist && onOpen && (
          <Button size="sm" variant="outline" className="h-7" data-testid={`triage-open-${idx}`} onClick={() => onOpen(item, adapter)}>
            <ExternalLink className="h-3.5 w-3.5 mr-1" />{adapter.specialist.label || "Open"}
          </Button>
        )}
      </div>
    );
  }

  const grouped = SECTION_ORDER.map((s) => ({ section: s, items: menu.filter((m) => m.section === s) })).filter((g) => g.items.length);

  return (
    <div className="flex items-center gap-1.5 shrink-0" data-testid={`triage-${idx}`}>
      {/* Responsibility shown as state — click corrects it (route). */}
      {reassign && (
        <button className={`text-[11px] underline decoration-dotted ${owner ? "text-slate-600 hover:text-indigo-700" : "text-amber-700 hover:text-amber-900"}`} data-testid={`triage-owner-${idx}`} onClick={() => startAction(reassign)}>
          {owner || "Unassigned"}
        </button>
      )}
      {/* A real linked client that is a PARENT (task adapter) can be opened/relinked. */}
      {client.id && (
        <button className="text-[11px] text-indigo-700 hover:text-indigo-900 underline" data-testid={`triage-client-open-${idx}`} onClick={() => onOpenClient && onOpenClient(client.id)}>
          {client.name || "Open client"}
        </button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-7" data-testid={`triage-menu-${idx}`}>Triage <ChevronDown className="h-3 w-3 ml-1" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {grouped.map((g, gi) => (
            <React.Fragment key={g.section}>
              {gi > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">{SECTION_LABEL[g.section]}</DropdownMenuLabel>
              {g.items.map((m) => (
                <DropdownMenuItem key={m.key} data-testid={`triage-act-${m.key}-${idx}`} onClick={() => startAction(m)}>{m.label}</DropdownMenuItem>
              ))}
            </React.Fragment>
          ))}
          <DropdownMenuSeparator />
          {adapter.historyRead && (
            <DropdownMenuItem data-testid={`triage-act-history-${idx}`} onClick={openHistory}><History className="h-3.5 w-3.5 mr-2" /> View history</DropdownMenuItem>
          )}
          {adapter.specialist && onOpen && (
            <DropdownMenuItem data-testid={`triage-act-open-${idx}`} onClick={() => onOpen(item, adapter)}><ExternalLink className="h-3.5 w-3.5 mr-2" /> {adapter.specialist.label}</DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!open} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent className="max-w-md" data-testid={`triage-dialog-${idx}`}>
          <DialogHeader><DialogTitle>{open?.key === "__history" ? "History" : (open?.label || "Triage")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">{title}</DialogDescription>
          <p className="text-sm text-slate-600 truncate">{title}</p>

          {open?.key === "__history" ? (
            <div className="max-h-72 overflow-y-auto space-y-2" data-testid={`triage-history-${idx}`}>
              {history?.loading ? <p className="text-xs text-slate-400">Loading…</p> :
                (history?.rows || []).length === 0 ? <p className="text-xs text-slate-400">No recorded history yet.</p> :
                history.rows.map((h, hi) => {
                  const ch = h.changes || (h.field ? [{ field: h.field, from: h.from, to: h.to }] : []);
                  const who = h.actor_name || h.user_name || h.actor || "someone";
                  const when = (h.timestamp || "").slice(0, 16).replace("T", " ");
                  const why = h.reason || h.why || "";
                  return (
                    <div key={hi} className="text-xs border border-slate-100 rounded px-2 py-1.5">
                      <div className="font-medium text-slate-700">{h.action_label || h.action || h.route || "change"} · {who} <span className="text-slate-400">{when}</span></div>
                      {ch.map((c, ci) => <div key={ci} className="text-slate-500">{c.field}: <span className="line-through">{String(c.from ?? "—")}</span> → {String(c.to ?? "—")}</div>)}
                      {why && <div className="text-slate-500 italic">“{why}”</div>}
                    </div>
                  );
                })}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {(open?.inputs || []).map((inp) => (
                  <div key={inp.name}>
                    <Label>{inp.label}{inp.required ? " *" : ""}</Label>
                    {inp.kind === "date" && <Input type="date" data-testid={`${inputTestId(inp.name)}-${idx}`} value={vals[inp.name] || ""} onChange={(e) => setVals({ ...vals, [inp.name]: e.target.value })} />}
                    {inp.kind === "text" && <Input data-testid={`${inputTestId(inp.name)}-${idx}`} value={vals[inp.name] || ""} onChange={(e) => setVals({ ...vals, [inp.name]: e.target.value })} />}
                    {inp.kind === "textarea" && <Textarea rows={2} data-testid={`${inputTestId(inp.name)}-${idx}`} value={vals[inp.name] || ""} onChange={(e) => setVals({ ...vals, [inp.name]: e.target.value })} placeholder={inp.placeholder || ""} />}
                    {inp.kind === "select" && (
                      <select className="w-full text-sm border border-slate-300 rounded px-2 py-1.5" data-testid={`${inputTestId(inp.name)}-${idx}`} value={vals[inp.name] || ""} onChange={(e) => setVals({ ...vals, [inp.name]: e.target.value })}>
                        <option value="">Choose…</option>
                        {inp.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                    {inp.kind === "user" && (
                      <select className="w-full text-sm border border-slate-300 rounded px-2 py-1.5" data-testid={`${inputTestId(inp.name)}-${idx}`} value={vals[inp.name] || ""} onChange={(e) => setVals({ ...vals, [inp.name]: e.target.value })}>
                        <option value="">Choose a person…</option>
                        {users.map((u) => <option key={u.user_id || u.id} value={u.user_id || u.id}>{u.full_name || u.name || u.email}</option>)}
                      </select>
                    )}
                    {inp.kind === "client-search" && (
                      <div>
                        <Input data-testid={`triage-relate-search-${idx}`} value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Type a company name…" />
                        {vals[inp.name] && <p className="text-xs text-emerald-700 mt-1">Selected: {vals[inp.name].company_name}</p>}
                        <div className="mt-1 max-h-40 overflow-y-auto border border-slate-100 rounded divide-y divide-slate-100">
                          {results.length === 0 ? <p className="text-xs text-slate-400 px-2 py-1.5">No matches — we never guess the client.</p> :
                            results.map((c) => <button key={c.client_id} type="button" className="w-full text-left px-2 py-1 text-sm hover:bg-indigo-50" data-testid={`triage-relate-pick-${idx}-${c.client_id}`} onClick={() => { setVals({ ...vals, [inp.name]: c }); setResults([]); }}>{c.company_name}</button>)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {err && <p className="text-xs text-rose-600" data-testid={`triage-error-${idx}`}>{err}</p>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close}>Cancel</Button>
                <Button data-testid={`triage-save-${idx}`} disabled={busy} onClick={() => runAction(open, vals)}>Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
